import React, {memo, ReactElement, useEffect} from "react";
import {Props} from "./VirtualizedList.types";
import {useMagicReducer} from "@witivio_teamspro/use-reducer";
import {initialState, reducer} from "./VirtualizedList.reducer";
import {CompareModule} from "modules/Compare.module";
import "./VirtualizedList.styles.scss";
import {Flex, Loader} from "@fluentui/react-northstar";
import {Swiper, SwiperSlide} from "swiper/react";
import {FreeMode, Mousewheel, Scrollbar, Virtual} from "swiper/modules";

export const VirtualizedList = memo((props: Props): ReactElement | null => {
    const [state, dispatch] = useMagicReducer(reducer, initialState);

    useEffect(() => {
        if (!state.container) return;
        const resizeObserver = new ResizeObserver(() => dispatch("containerRef")(state.container as HTMLDivElement));
        resizeObserver.observe(state.container as HTMLDivElement);
        return () => {
            if (!state.container) return;
            resizeObserver.unobserve(state.container as HTMLDivElement);
        }
    }, [state.container]);

    useEffect(() => {
        if (!state.scrollbar) return;
        const resizeObserver = new ResizeObserver(dispatch("scrollbarResized"));
        resizeObserver.observe(state.scrollbar as HTMLDivElement);
        return () => {
            if (!state.scrollbar) return;
            resizeObserver.unobserve(state.scrollbar as HTMLDivElement);
        }
    }, [state.scrollbar]);

    const showLoader = !state.swiperHeight || !state.itemHeight;

    const slidesPerView = getSlidesPerView(state.swiperHeight, state.itemHeight);

    if (props.items.length === 0) return null;

    return (
        <Flex fill column ref={dispatch("containerRef")}>
            <div style={{position: "absolute", visibility: "hidden", height: "fit-content", pointerEvents: "none"}}
                 ref={dispatch("setItemHeight")}>
                {props.items[0]}
            </div>
            {showLoader ?
                <Flex fill hAlign={"center"} vAlign={"center"}>
                    <Loader/>
                </Flex>
                :
                <Flex fill className={"virtualized-list"} hAlign={"center"} vAlign={"center"}
                      styles={{height: state.swiperHeight + "px"}}>
                    <Swiper
                        onSwiper={dispatch("swiperRef")}
                        modules={[Virtual, FreeMode, Mousewheel, Scrollbar]}
                        slidesPerView={slidesPerView}
                        direction={"vertical"}
                        freeMode
                        style={{padding: "0 15px"}}
                        mousewheel={{
                            releaseOnEdges: true,
                            forceToAxis: true,
                        }}
                        onScroll={dispatch("scroll")}
                        touchReleaseOnEdges
                        scrollbar={{draggable: true}}
                        virtual>
                        {props.items.map((slideContent, index) => (
                            <SwiperSlide key={slideContent.key} virtualIndex={index}>
                                {slideContent}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Flex>
            }
        </Flex>
    )
}, CompareModule.areObjectsEqual);

///////////////////////////////////////////////////// PURE METHODS /////////////////////////////////////////////////////

const getSlidesPerView = (containerHeight: number, elementHeight: number) => {
    const exactFit = containerHeight / elementHeight;
    return exactFit * 0.76;
}